import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { 
  TrendingUp, TrendingDown, Wallet, Building2, Users, Zap,
  Receipt, AlertTriangle, Briefcase, PiggyBank, RefreshCw, 
  Loader2, ChevronDown, ChevronUp, Calendar, IndianRupee,
  ArrowUpRight, ArrowDownRight, Banknote, CreditCard, Clock, History,
  Target, Building, FileDown, Send, Settings, HelpCircle, X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

const CashFlowStatement = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [hideZeroTabs, setHideZeroTabs] = useState(false);
  const [viewMode, setViewMode] = useState('scrollable'); // 'scrollable' or 'detailed'
  const [accountingBasis, setAccountingBasis] = useState('cash'); // 'cash' or 'accrual'
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [unmappedCategories, setUnmappedCategories] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    inflows: true,
    loan_funds_received: true,
    food_expenses: true,
    fixed_expenses: true,
    investment_expenses: true,
    marketing_expenses: true,
    permits_expenses: true,
    events_expenses: true,
    unwanted_expenses: true,
    old_month_payments: true,
    opening_liabilities_expenses: true,
    management_withdrawals: true,
    project_bakery_expenses: true,
    loan_repayments: true,
    other_uncaptured_expenses: true,
    closing_value: true,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // PDF Export handler
  const handleExportPdf = async (exportOption) => {
    setExportingPdf(true);
    try {
      // Parse export option: "scrollable", "detailed", "scrollable_with_ledger", "detailed_with_ledger"
      const [viewMode, withLedger] = exportOption.includes('_with_ledger') 
        ? [exportOption.replace('_with_ledger', ''), true]
        : [exportOption, false];
      
      let url = `${API}/cash-flow/export-pdf?period=${period}&view_mode=${viewMode}&include_expense_ledger=${withLedger}`;
      if (period === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const suffix = withLedger ? '_with_ExpenseLedger' : '';
      link.download = `CashFlow_${viewMode}${suffix}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${viewMode === 'detailed' ? 'Detailed' : 'Summary'} PDF ${withLedger ? 'with Expense Ledger ' : ''}downloaded successfully!`);
    } catch (error) {
      toast.error(`Failed to export PDF: ${getErrorMessage(error)}`);
    } finally {
      setExportingPdf(false);
    }
  };

  // Send to Telegram handler
  const handleSendTelegram = async () => {
    setSendingTelegram(true);
    try {
      let url = `${API}/cash-flow/send-telegram?period=${period}`;
      if (period === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      const response = await axios.post(url, null, { headers });
      
      if (response.data.success) {
        toast.success(`Reports sent to ${response.data.success_count}/${response.data.total_recipients} recipients!`);
      } else {
        toast.error('Failed to send reports');
      }
    } catch (error) {
      toast.error(`Failed to send Telegram: ${getErrorMessage(error)}`);
    } finally {
      setSendingTelegram(false);
    }
  };

  // Fetch unmapped categories for admin
  const fetchUnmappedCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${API}/cash-flow/admin/unmapped-categories`, { headers });
      setUnmappedCategories(response.data);
      setShowCategoryManager(true);
    } catch (error) {
      toast.error(`Failed to fetch categories: ${getErrorMessage(error)}`);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Map category to a Cash Flow section
  const mapCategoryToSection = async (categoryId, targetGroup) => {
    try {
      const response = await axios.post(
        `${API}/cash-flow/admin/map-category-to-section?category_id=${categoryId}&target_group=${encodeURIComponent(targetGroup)}`,
        null,
        { headers }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        fetchUnmappedCategories(); // Refresh the list
        fetchData(); // Refresh cash flow data
      }
    } catch (error) {
      toast.error(`Failed to map category: ${getErrorMessage(error)}`);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/cash-flow/statement?period=${period}&accounting_basis=${accountingBasis}`;
      if (period === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      const response = await axios.get(url, { headers });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch cash flow:', error);
      toast.error(getErrorMessage(error, 'Failed to load cash flow statement'));
    } finally {
      setLoading(false);
    }
  }, [period, customStartDate, customEndDate, accountingBasis]);

  useEffect(() => {
    if (period !== 'custom' || (customStartDate && customEndDate)) {
      fetchData();
    }
  }, [period, fetchData, accountingBasis]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getPeriodLabel = () => {
    if (!data) return '';
    const { start_date, end_date } = data.period;
    if (start_date === end_date) {
      return new Date(start_date).toLocaleDateString('en-IN', { 
        day: 'numeric', month: 'short', year: 'numeric' 
      });
    }
    return `${new Date(start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${new Date(end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Wallet className="w-7 h-7 text-blue-600" />
            Cash Flow Statement
          </h1>
          {data && (
            <p className="text-gray-500 text-sm mt-1">
              {getPeriodLabel()} • {data.day_closings_count} day closing(s) • 
              <span className={`ml-1 font-medium ${accountingBasis === 'cash' ? 'text-green-600' : 'text-blue-600'}`}>
                {accountingBasis === 'cash' ? '💵 Cash Basis' : '📅 Accrual Basis'}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-48" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scrollable">Scrollable Cash Flow</SelectItem>
              <SelectItem value="detailed">Detailed Cash Flow</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Accounting Basis Toggle */}
          <Select value={accountingBasis} onValueChange={setAccountingBasis}>
            <SelectTrigger className="w-36" data-testid="accounting-basis-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-green-600" />
                  <span>Cash Basis</span>
                </div>
              </SelectItem>
              <SelectItem value="accrual">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Accrual Basis</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* Hide Zero Tabs Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 hover:text-gray-800">
            <input
              type="checkbox"
              checked={hideZeroTabs}
              onChange={(e) => setHideZeroTabs(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              data-testid="hide-zero-tabs-checkbox"
            />
            <span>Hide ₹0 tabs</span>
          </label>
          
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          
          {/* PDF Export Dropdown */}
          <Select 
            value="" 
            onValueChange={(value) => handleExportPdf(value)}
            disabled={exportingPdf || loading}
          >
            <SelectTrigger className="w-40" data-testid="export-pdf-btn">
              <div className="flex items-center gap-2">
                {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                <span>Export PDF</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scrollable">Summary PDF</SelectItem>
              <SelectItem value="detailed">Detailed PDF</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Send to Telegram Button (Admin/Partner only) */}
          {(user?.role === 'admin' || user?.role === 'partner') && (
            <Button
              variant="default"
              size="sm"
              onClick={handleSendTelegram}
              disabled={sendingTelegram || loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-telegram-btn"
            >
              {sendingTelegram ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to Telegram
            </Button>
          )}
          
          {/* Category Manager Button (Admin only) */}
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUnmappedCategories}
              disabled={loadingCategories}
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="category-manager-btn"
            >
              {loadingCategories ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Settings className="w-4 h-4 mr-2" />
              )}
              Category Manager
            </Button>
          )}
        </div>
      </div>

      {/* Category Manager Modal */}
      {showCategoryManager && unmappedCategories && (
        <Card className="border-2 border-amber-300 bg-amber-50/30 mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-lg text-amber-800">Category Manager</CardTitle>
                <Badge variant="outline" className="ml-2 bg-amber-100">
                  {unmappedCategories.total_unmapped} unmapped
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCategoryManager(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              Map expense categories to Cash Flow sections. Unmapped categories contribute to &quot;Uncaptured Expenses&quot;.
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            {unmappedCategories.unmapped_categories.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {unmappedCategories.unmapped_categories.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div>
                      <span className="font-medium">{cat.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({cat.group})</span>
                    </div>
                    <Select
                      onValueChange={(value) => mapCategoryToSection(cat.id, value)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Map to section..." />
                      </SelectTrigger>
                      <SelectContent>
                        {unmappedCategories.available_cash_flow_sections.map((section) => (
                          <SelectItem key={section.id} value={section.group_pattern}>
                            {section.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                All categories are mapped to Cash Flow sections!
              </p>
            )}
            
            {unmappedCategories.missing_from_expense_categories.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">
                  Categories used in deductions but not defined:
                </p>
                <div className="space-y-1">
                  {unmappedCategories.missing_from_expense_categories.slice(0, 5).map((cat, idx) => (
                    <div key={idx} className="text-sm text-red-600">
                      • {cat.name}
                    </div>
                  ))}
                  {unmappedCategories.missing_from_expense_categories.length > 5 && (
                    <div className="text-sm text-red-500">
                      ... and {unmappedCategories.missing_from_expense_categories.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Custom Date Range */}
      {period === 'custom' && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-sm">Start Date</Label>
                <Input 
                  type="date" 
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-40"
                  data-testid="start-date"
                />
              </div>
              <div>
                <Label className="text-sm">End Date</Label>
                <Input 
                  type="date" 
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-40"
                  data-testid="end-date"
                />
              </div>
              <Button 
                onClick={fetchData}
                disabled={!customStartDate || !customEndDate || loading}
                data-testid="apply-custom-range"
              >
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards - Two Boxes */}
      {data && (
        <div className="space-y-4">
          {/* Box 1: Flow Summary */}
          <Card className="border-2 border-blue-200 bg-blue-50/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-blue-700">Cash Flow Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard 
                  label="Total Inflows" 
                  value={data.summary.total_inflows} 
                  icon={ArrowUpRight}
                  color="green"
                />
                <SummaryCard 
                  label="Restaurant Outflows" 
                  value={data.summary.restaurant_outflows} 
                  icon={Receipt}
                  color="orange"
                />
                <SummaryCard 
                  label="Withdrawals Outflow" 
                  value={data.summary.withdrawals_outflow} 
                  icon={ArrowDownRight}
                  color="red"
                />
                <SummaryCard 
                  label="Available Cash Flow" 
                  value={data.summary.available_cash_flow} 
                  icon={data.summary.available_cash_flow >= 0 ? TrendingUp : TrendingDown}
                  color={data.summary.available_cash_flow >= 0 ? 'blue' : 'red'}
                />
              </div>
            </CardContent>
          </Card>

          {/* Box 2: Value Summary */}
          <Card className="border-2 border-purple-200 bg-purple-50/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-purple-700">Balance Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <SummaryCard 
                  label="Opening Value" 
                  value={data.summary.opening_value} 
                  icon={Wallet}
                  color="blue"
                  cashValue={data.opening_value?.cash_in_hand}
                  bankValue={data.opening_value?.bank_balance}
                />
                <SummaryCard 
                  label="Net Cash Flow" 
                  value={data.summary.net_cash_flow} 
                  icon={data.summary.net_cash_flow >= 0 ? TrendingUp : TrendingDown}
                  color={data.summary.net_cash_flow >= 0 ? 'green' : 'red'}
                />
                <SummaryCard 
                  label="Closing Value" 
                  value={data.summary.closing_value} 
                  icon={PiggyBank}
                  color="purple"
                  cashValue={data.closing_value?.cash_in_hand}
                  bankValue={data.closing_value?.bank_balance}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cash Flow Sections */}
      {data && (
        <div className="space-y-4">
          {/* INFLOWS Section */}
          {(!hideZeroTabs || data.inflows.total_sales > 0) && (
            <CashFlowSection
              title="Inflows (Revenue)"
              icon={TrendingUp}
              color="green"
              total={data.inflows.total_sales}
              expanded={expandedSections.inflows}
              onToggle={() => toggleSection('inflows')}
              details={data.inflows.details}
              viewMode={viewMode}
            />
          )}

          {/* LOAN FUNDS RECEIVED Section */}
          {(!hideZeroTabs || (data.loan_funds_received?.total || 0) > 0) && (
            <CashFlowSection
              title="Loan Funds Received"
              icon={ArrowDownRight}
              color="green"
              total={data.loan_funds_received?.total || 0}
              expanded={expandedSections.loan_funds_received}
              onToggle={() => toggleSection('loan_funds_received')}
              details={data.loan_funds_received?.details || []}
              subtitle="Funds transferred from loan accounts to bank"
              viewMode={viewMode}
            />
          )}

          {/* FOOD EXPENSES Section */}
          {(!hideZeroTabs || data.food_expenses.total > 0) && (
            <CashFlowSection
              title="Food Expenses (Vendor Payments)"
              icon={Building2}
              color="orange"
              total={data.food_expenses.total}
              expanded={expandedSections.food_expenses}
              onToggle={() => toggleSection('food_expenses')}
              details={data.food_expenses.details}
              subtitle={`Cash: ${formatCurrency(data.food_expenses.cash_payments)} | Bank: ${formatCurrency(data.food_expenses.bank_payments)}`}
              viewMode={viewMode}
            />
          )}

          {/* FIXED EXPENSES Section - with dropdown for Salaries and Utilities */}
          {(!hideZeroTabs || data.fixed_expenses.total > 0) && (
            <FixedExpensesSection
              data={data.fixed_expenses}
              expanded={expandedSections.fixed_expenses}
              onToggle={() => toggleSection('fixed_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* INVESTMENT EXPENSES Section */}
          {(!hideZeroTabs || (data.investment_expenses?.total || 0) > 0) && (
            <SimpleBreakdownSection
              title="Investment"
              icon={TrendingUp}
              color="green"
              data={data.investment_expenses || { total: 0, details: [] }}
              expanded={expandedSections.investment_expenses}
              onToggle={() => toggleSection('investment_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* MARKETING EXPENSES Section */}
          {(!hideZeroTabs || (data.marketing_expenses?.total || 0) > 0) && (
            <SimpleBreakdownSection
              title="Marketing"
              icon={Target}
              color="orange"
              data={data.marketing_expenses || { total: 0, details: [] }}
              expanded={expandedSections.marketing_expenses}
              onToggle={() => toggleSection('marketing_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* PERMITS Section */}
          {(!hideZeroTabs || (data.permits_expenses?.total || 0) > 0) && (
            <SimpleBreakdownSection
              title="Permits & Licenses"
              icon={Receipt}
              color="cyan"
              data={data.permits_expenses || { total: 0, details: [] }}
              expanded={expandedSections.permits_expenses}
              onToggle={() => toggleSection('permits_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* EVENTS Section */}
          {(!hideZeroTabs || (data.events_expenses?.total || 0) > 0) && (
            <SimpleBreakdownSection
              title="Events"
              icon={Briefcase}
              color="pink"
              data={data.events_expenses || { total: 0, details: [] }}
              expanded={expandedSections.events_expenses}
              onToggle={() => toggleSection('events_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* UNWANTED EXPENSES Section - Special nested breakdown */}
          {(!hideZeroTabs || data.unwanted_expenses.total > 0) && (
            <UnwantedExpensesSection
              data={data.unwanted_expenses}
              expanded={expandedSections.unwanted_expenses}
              onToggle={() => toggleSection('unwanted_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* OLD MONTH PAYMENTS Section */}
          {(!hideZeroTabs || (data.old_month_payments?.total || 0) > 0) && (
            <CashFlowSection
              title="Old Month Payments"
              icon={Clock}
              color="amber"
              total={data.old_month_payments?.total || 0}
              expanded={expandedSections.old_month_payments}
              onToggle={() => toggleSection('old_month_payments')}
              details={data.old_month_payments?.details || []}
              subtitle={data.old_month_payments?.total > 0 ? 
                `Vendor Bills: ${formatCurrency(data.old_month_payments?.vendor_bills)} | Salaries: ${formatCurrency(data.old_month_payments?.salaries)}` : 
                'No old month payments in this period'
              }
              viewMode={viewMode}
            />
          )}

          {/* OPENING LIABILITIES EXPENSES Section */}
          {(!hideZeroTabs || (data.opening_liabilities_expenses?.total || 0) > 0) && (
            <CashFlowSection
              title="Opening Liabilities Expenses"
              icon={History}
              color="indigo"
              total={data.opening_liabilities_expenses?.total || 0}
              expanded={expandedSections.opening_liabilities_expenses}
              onToggle={() => toggleSection('opening_liabilities_expenses')}
              details={data.opening_liabilities_expenses?.details || []}
              subtitle={data.opening_liabilities_expenses?.total > 0 ? 
                `Vendors: ${formatCurrency(data.opening_liabilities_expenses?.vendor_liabilities)} | Employees: ${formatCurrency(data.opening_liabilities_expenses?.employee_liabilities)} | Utilities: ${formatCurrency(data.opening_liabilities_expenses?.utility_liabilities)}` : 
                'No opening liability payments in this period'
              }
              viewMode={viewMode}
            />
          )}

          {/* MANAGEMENT WITHDRAWALS Section - Special nested breakdown */}
          {(!hideZeroTabs || data.management_withdrawals.total > 0) && (
            <ManagementWithdrawalsSection
              data={data.management_withdrawals}
              expanded={expandedSections.management_withdrawals}
              onToggle={() => toggleSection('management_withdrawals')}
              viewMode={viewMode}
            />
          )}

          {/* PROJECT - BAKERY Section */}
          {(!hideZeroTabs || (data.project_bakery_expenses?.total || 0) > 0) && (
            <SimpleBreakdownSection
              title="Project - Bakery"
              icon={Building}
              color="amber"
              data={data.project_bakery_expenses || { total: 0, details: [] }}
              expanded={expandedSections.project_bakery_expenses}
              onToggle={() => toggleSection('project_bakery_expenses')}
              viewMode={viewMode}
            />
          )}

          {/* LOAN REPAYMENTS Section */}
          {(!hideZeroTabs || (data.loan_repayments?.total || 0) > 0) && (
            <CashFlowSection
              title="Loan Repayments (Principal)"
              icon={ArrowUpRight}
              color="purple"
              total={data.loan_repayments?.total || 0}
              expanded={expandedSections.loan_repayments}
              onToggle={() => toggleSection('loan_repayments')}
              details={data.loan_repayments?.details || []}
              subtitle="Principal payments made to loan accounts"
              viewMode={viewMode}
            />
          )}

          {/* UNCAPTURED EXPENSES Section - Show if > 0 */}
          {(data.other_uncaptured_expenses?.total > 0) && (
            <Card className="border-2 border-amber-300 bg-amber-50/30">
              <CardHeader 
                className="cursor-pointer hover:bg-amber-50/50 transition-colors"
                onClick={() => toggleSection('other_uncaptured_expenses')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Uncaptured Expenses</CardTitle>
                      <p className="text-sm text-amber-700">
                        {data.other_uncaptured_expenses?.note || 'Expenses not mapped to categories'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-amber-600">
                      {formatCurrency(data.other_uncaptured_expenses?.total || 0)}
                    </span>
                    {expandedSections.other_uncaptured_expenses ? 
                      <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    }
                  </div>
                </div>
              </CardHeader>
              {expandedSections.other_uncaptured_expenses && (
                <CardContent className="pt-0">
                  <div className="p-4 bg-amber-100/50 rounded-lg">
                    <p className="text-sm text-amber-800 mb-3">
                      This amount represents bank debits and cash expenses that could not be categorized 
                      into specific Cash Flow sections. It is calculated to balance the equation:
                    </p>
                    <div className="text-sm font-mono bg-white p-2 rounded border border-amber-200 mb-3">
                      Opening + Inflows - Outflows = Closing
                    </div>
                    <p className="text-sm text-amber-700">
                      To reduce this amount, use the <strong>Category Manager</strong> button above 
                      to map expense categories to Cash Flow sections, or ensure bank transactions 
                      have proper [GROUP/CATEGORY] references.
                    </p>
                    {user?.role === 'admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchUnmappedCategories}
                        className="mt-3 border-amber-500 text-amber-700 hover:bg-amber-100"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Open Category Manager
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* CLOSING VALUE Section - Always show */}
          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardHeader 
              className="cursor-pointer hover:bg-purple-50/50 transition-colors"
              onClick={() => toggleSection('closing_value')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <PiggyBank className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Closing Cash Value</CardTitle>
                    <p className="text-sm text-gray-500">Cash + Bank Combined</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-purple-600">
                    {formatCurrency(data.closing_value.total)}
                  </span>
                  {expandedSections.closing_value ? 
                    <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  }
                </div>
              </div>
            </CardHeader>
            {expandedSections.closing_value && (
              <CardContent className="pt-0">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Cash in Hand */}
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Cash in Hand</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(data.closing_value.cash_in_hand)}
                    </p>
                  </div>
                  
                  {/* Bank Balance */}
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Bank Balance</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(data.closing_value.bank_balance)}
                    </p>
                  </div>
                </div>
                
                {/* Bank Account Breakdown */}
                {data.closing_value.bank_accounts?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Bank Account Breakdown</p>
                    <div className="bg-white rounded-lg border divide-y">
                      {data.closing_value.bank_accounts.map((acc, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3">
                          <span className="text-gray-700">{acc.account}</span>
                          <span className={`font-semibold ${acc.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(acc.balance)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ label, value, icon: Icon, color, cashValue, bankValue }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
  };

  const classes = colorClasses[color] || colorClasses.blue;
  const [bgColor, textColor, borderColor] = classes.split(' ');

  const showBreakdown = cashValue !== undefined && bankValue !== undefined;

  return (
    <div className={`rounded-xl p-4 border-2 ${bgColor} ${borderColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${textColor}`} />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <p className={`text-xl md:text-2xl font-bold ${textColor}`}>
        {formatCurrency(value)}
      </p>
      {showBreakdown && (
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Cash:</span>
            <span className="font-medium text-gray-700">{formatCurrency(cashValue)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Bank:</span>
            <span className="font-medium text-gray-700">{formatCurrency(bankValue)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Cash Flow Section Component
const CashFlowSection = ({ 
  title, 
  icon: Icon, 
  color, 
  total, 
  expanded, 
  onToggle, 
  details,
  subtitle,
  extraDetails,
  viewMode = 'scrollable'
}) => {
  const colorClasses = {
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600', text: 'text-green-600' },
    red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600', text: 'text-red-600' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600', text: 'text-purple-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600', text: 'text-orange-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', text: 'text-amber-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-600', text: 'text-indigo-600' },
  };

  const colors = colorClasses[color] || colorClasses.blue;
  const isDetailed = viewMode === 'detailed';
  const isExpanded = isDetailed || expanded;

  return (
    <Card className={`border-2 ${colors.border} ${colors.bg}/30`}>
      <CardHeader 
        className={`${!isDetailed ? 'cursor-pointer hover:' + colors.bg + '/50' : ''} transition-colors`}
        onClick={!isDetailed ? onToggle : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${colors.icon} flex items-center justify-center`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-xl md:text-2xl font-bold ${colors.text}`}>
              {formatCurrency(total)}
            </span>
            {!isDetailed && (
              isExpanded ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && details && details.length > 0 && (
        <CardContent className="pt-0">
          <div className={`bg-white rounded-lg border divide-y ${!isDetailed ? 'max-h-60 overflow-y-auto' : ''}`}>
            {details.map((item, idx) => (
              <div key={idx} className="flex items-center px-4 py-3 hover:bg-gray-50">
                <span className="font-semibold text-gray-900 w-28 text-left">{formatCurrency(item.amount)}</span>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-gray-700">{item.label}</span>
                  {item.source && (
                    <Badge variant="outline" className="text-xs">
                      {item.source}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Extra Details (e.g., utility breakdown) */}
          {extraDetails && extraDetails.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Detailed Breakdown</p>
              <div className={`bg-white rounded-lg border divide-y ${!isDetailed ? 'max-h-40 overflow-y-auto' : ''}`}>
                {extraDetails.map((item, idx) => (
                  <div key={idx} className="flex items-center px-4 py-2 text-sm hover:bg-gray-50">
                    <span className="font-medium text-gray-800 w-28 text-left">{formatCurrency(item.amount)}</span>
                    <span className="text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Special Unwanted Expenses Section with nested dropdowns
const UnwantedExpensesSection = ({ data, expanded, onToggle, viewMode = 'scrollable' }) => {
  const isDetailed = viewMode === 'detailed';
  const isExpanded = isDetailed || expanded;
  
  const [expandedItems, setExpandedItems] = useState({
    discounts: isDetailed,
    nc: isDetailed,
    commissions: isDetailed,
    misc: isDetailed
  });

  const toggleItem = (item, e) => {
    if (isDetailed) return; // Don't toggle in detailed mode
    e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  // In detailed mode, all items should be expanded
  const showDiscounts = isDetailed || expandedItems.discounts;
  const showNc = isDetailed || expandedItems.nc;
  const showCommissions = isDetailed || expandedItems.commissions;
  const showMisc = isDetailed || expandedItems.misc;

  return (
    <Card className="border-2 border-red-200 bg-red-50/30">
      <CardHeader 
        className={`${!isDetailed ? 'cursor-pointer hover:bg-red-50/50' : ''} transition-colors`}
        onClick={!isDetailed ? onToggle : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Unwanted Expenses (Discounts, NC, Commissions, Misc)</CardTitle>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl md:text-2xl font-bold text-red-600">
              {formatCurrency(data.total)}
            </span>
            {!isDetailed && (
              isExpanded ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-2">
          {/* First Level: Discounts Given */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div 
              className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              onClick={!isDetailed ? (e) => toggleItem('discounts', e) : undefined}
            >
              <div className="flex items-center gap-2">
                {!isDetailed && data.discounts_breakdown?.length > 0 && (
                  expandedItems.discounts ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-gray-700 font-medium">Discounts Given</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(data.discounts)}</span>
            </div>
            {/* Second Level: Discounts breakdown - category wise from DISCOUNTS group */}
            {showDiscounts && data.discounts_breakdown?.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Details</p>
                <div className="space-y-2">
                  {data.discounts_breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* First Level: NC (Non-Chargeable) */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div 
              className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              onClick={!isDetailed ? (e) => toggleItem('nc', e) : undefined}
            >
              <div className="flex items-center gap-2">
                {!isDetailed && data.nc_breakdown?.length > 0 && (
                  expandedItems.nc ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-gray-700 font-medium">NC (Non-Chargeable)</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(data.nc_amount)}</span>
            </div>
            {/* Second Level: NC breakdown - category wise from NC CATEGORY group */}
            {showNc && data.nc_breakdown?.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Details</p>
                <div className="space-y-2">
                  {data.nc_breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* First Level: Commissions */}
          {data.commissions > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div 
                className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={!isDetailed ? (e) => toggleItem('commissions', e) : undefined}
              >
                <div className="flex items-center gap-2">
                  {!isDetailed && data.commissions_breakdown?.length > 0 && (
                    expandedItems.commissions ? 
                      <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-gray-700 font-medium">Commissions</span>
                </div>
                <span className="font-semibold text-gray-900">{formatCurrency(data.commissions)}</span>
              </div>
              {/* Second Level: Commissions breakdown */}
              {showCommissions && data.commissions_breakdown?.length > 0 && (
                <div className="border-t bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Details</p>
                  <div className="space-y-2">
                    {data.commissions_breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                        <span className="text-sm text-gray-600">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* First Level: Misc/Extra/Charges */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div 
              className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              onClick={!isDetailed ? (e) => toggleItem('misc', e) : undefined}
            >
              <div className="flex items-center gap-2">
                {!isDetailed && data.misc_breakdown?.length > 0 && (
                  expandedItems.misc ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-gray-700 font-medium">Misc/Extra/Charges</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(data.miscellaneous)}</span>
            </div>
            {/* Second Level: Misc breakdown with groups and categories */}
            {showMisc && data.misc_breakdown?.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Detailed Breakdown</p>
                <div className="space-y-3">
                  {data.misc_breakdown.map((groupItem, idx) => (
                    <div key={idx} className="space-y-1">
                      {/* Group Header with Total */}
                      {groupItem.categories?.length > 0 && (
                        <div className="flex items-center justify-between mt-2 border-b border-gray-200 pb-1">
                          <p className="text-xs font-semibold text-gray-600 uppercase">{groupItem.group}</p>
                          <span className="text-sm font-bold text-gray-700">{formatCurrency(groupItem.total)}</span>
                        </div>
                      )}
                      {/* Categories under this group - amounts left aligned */}
                      {groupItem.categories?.map((cat, catIdx) => (
                        <div key={catIdx} className="flex items-center pl-2">
                          <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(cat.amount)}</span>
                          <span className="text-sm text-gray-600">{cat.label}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Fixed Expenses Section with nested dropdown for Salaries (Payroll + Advance) and Utilities (category-wise)
const FixedExpensesSection = ({ data, expanded, onToggle, viewMode = 'scrollable' }) => {
  const isDetailed = viewMode === 'detailed';
  const isExpanded = isDetailed || expanded;
  
  const [expandedItems, setExpandedItems] = useState({
    utilities: isDetailed,
    salaries: isDetailed
  });

  const toggleItem = (item, e) => {
    if (isDetailed) return;
    e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const showUtilities = isDetailed || expandedItems.utilities;
  const showSalaries = isDetailed || expandedItems.salaries;

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/30">
      <CardHeader 
        className={`${!isDetailed ? 'cursor-pointer hover:bg-blue-50/50' : ''} transition-colors`}
        onClick={!isDetailed ? onToggle : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Fixed Expenses (Utilities + Salaries)</CardTitle>
              <p className="text-sm text-gray-500">
                Utilities: {formatCurrency(data.utilities)} | Salaries: {formatCurrency(data.salaries)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl md:text-2xl font-bold text-blue-600">
              {formatCurrency(data.total)}
            </span>
            {!isDetailed && (
              isExpanded ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-2">
          {/* Utility Bills */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div 
              className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              onClick={!isDetailed ? (e) => toggleItem('utilities', e) : undefined}
            >
              <div className="flex items-center gap-2">
                {!isDetailed && data.utility_breakdown?.length > 0 && (
                  expandedItems.utilities ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-gray-700 font-medium">Utility Bills</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(data.utilities)}</span>
            </div>
            {showUtilities && data.utility_breakdown?.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Category-wise</p>
                <div className="space-y-2">
                  {data.utility_breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Salaries */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div 
              className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              onClick={!isDetailed ? (e) => toggleItem('salaries', e) : undefined}
            >
              <div className="flex items-center gap-2">
                {!isDetailed && data.salary_breakdown?.length > 0 && (
                  expandedItems.salaries ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-gray-700 font-medium">Salaries</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(data.salaries)}</span>
            </div>
            {showSalaries && data.salary_breakdown?.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Breakdown</p>
                <div className="space-y-2">
                  {data.salary_breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Simple Breakdown Section for Investment, Marketing, Project-Bakery (single-level dropdown)
const SimpleBreakdownSection = ({ title, icon: Icon, color, data, expanded, onToggle, viewMode = 'scrollable' }) => {
  const isDetailed = viewMode === 'detailed';
  const isExpanded = isDetailed || expanded;
  
  const colorClasses = {
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600', text: 'text-green-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600', text: 'text-orange-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', text: 'text-amber-600' },
  };
  const colors = colorClasses[color] || colorClasses.green;

  return (
    <Card className={`border-2 ${colors.border} ${colors.bg}/30`}>
      <CardHeader 
        className={`${!isDetailed ? 'cursor-pointer hover:' + colors.bg + '/50' : ''} transition-colors`}
        onClick={!isDetailed ? onToggle : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${colors.icon} flex items-center justify-center`}>
              <Icon className="w-5 h-5" />
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-xl md:text-2xl font-bold ${colors.text}`}>
              {formatCurrency(data.total)}
            </span>
            {!isDetailed && (
              isExpanded ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && data.details?.length > 0 && (
        <CardContent className="pt-0">
          <div className="bg-white rounded-lg border px-4 py-3">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Category-wise Breakdown</p>
            <div className="space-y-2">
              {data.details.map((item, idx) => (
                <div key={idx} className="flex items-center">
                  <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Management Withdrawals Section with nested dropdowns for Rajan-Manjot, Gagan, Seerat
const ManagementWithdrawalsSection = ({ data, expanded, onToggle, viewMode = 'scrollable' }) => {
  const isDetailed = viewMode === 'detailed';
  const isExpanded = isDetailed || expanded;
  
  const [expandedItems, setExpandedItems] = useState({
    rajan_manjot: isDetailed,
    gagan: isDetailed
  });

  const toggleItem = (item, e) => {
    if (isDetailed) return;
    e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const showRajanManjot = isDetailed || expandedItems.rajan_manjot;
  const showGagan = isDetailed || expandedItems.gagan;

  return (
    <Card className="border-2 border-purple-200 bg-purple-50/30">
      <CardHeader 
        className={`${!isDetailed ? 'cursor-pointer hover:bg-purple-50/50' : ''} transition-colors`}
        onClick={!isDetailed ? onToggle : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Management Withdrawals</CardTitle>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xl md:text-2xl font-bold text-purple-600">
              {formatCurrency(data.total)}
            </span>
            {!isDetailed && (
              isExpanded ? 
                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-2">
          {/* Rajan-Manjot - ONLY show if amount > 0 */}
          {data.details?.find(d => d.label === 'Rajan-Manjot')?.amount > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div 
                className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={!isDetailed ? (e) => toggleItem('rajan_manjot', e) : undefined}
              >
                <div className="flex items-center gap-2">
                  {!isDetailed && data.rajan_manjot_breakdown?.length > 0 && (
                    expandedItems.rajan_manjot ? 
                      <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-gray-700 font-medium">Rajan-Manjot</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(data.details?.find(d => d.label === 'Rajan-Manjot')?.amount || 0)}
                </span>
              </div>
              {showRajanManjot && data.rajan_manjot_breakdown?.length > 0 && (
                <div className="border-t bg-gray-50 px-4 py-3">
                  <div className="space-y-2">
                    {data.rajan_manjot_breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                        <span className="text-sm text-gray-600">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gagan - ONLY show if amount > 0 */}
          {data.details?.find(d => d.label === 'Gagan')?.amount > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div 
                className={`flex items-center justify-between px-4 py-3 ${!isDetailed ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={!isDetailed ? (e) => toggleItem('gagan', e) : undefined}
              >
                <div className="flex items-center gap-2">
                  {!isDetailed && data.gagan_breakdown?.length > 0 && (
                    expandedItems.gagan ? 
                      <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-gray-700 font-medium">Gagan</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(data.details?.find(d => d.label === 'Gagan')?.amount || 0)}
                </span>
              </div>
              {showGagan && data.gagan_breakdown?.length > 0 && (
                <div className="border-t bg-gray-50 px-4 py-3">
                  <div className="space-y-2">
                    {data.gagan_breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-28 text-left">{formatCurrency(item.amount)}</span>
                        <span className="text-sm text-gray-600">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Seerat - No dropdown, just display - ONLY show if amount > 0 */}
          {data.details?.find(d => d.label === 'Seerat')?.amount > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-gray-700 font-medium">Seerat</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(data.details?.find(d => d.label === 'Seerat')?.amount || 0)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default CashFlowStatement;
